import { Cause, Effect, Exit, Ref, Scope } from "effect";
import { Atom, AtomRegistry } from "effect/unstable/reactivity";
import { INTERNAL_HTTP_MESSAGE } from "@workerstation/shared/errors/http";
import type { ResourceFailure } from "@workerstation/shared/errors/resource";
import { resourceFailureMessage } from "../../api/resources/failure.ts";
import { QueryWork } from "../../state/query-work.ts";
import { emptyCatalog, type CatalogSelection, type CatalogSession, type CatalogSnapshot } from "./model.ts";

interface State<A> {
  readonly closed: boolean;
  readonly refreshToken: number | undefined;
  readonly request: object | null;
  readonly snapshot: CatalogSnapshot<A>;
}
const closed = (scope: Scope.Scope): boolean => scope.state._tag === "Closed";
export const makeCatalogChildScope = Effect.fn("Frontend.Catalog.makeChildScope")((owner: Scope.Scope) => Scope.fork(owner));

export const makeCatalogSession = Effect.fn("Frontend.Catalog.makeSession")(function* <A>(root: Scope.Scope, load: (workspaceId: string) => Effect.Effect<A, ResourceFailure>) {
  const owner = yield* Scope.Scope, registry = yield* AtomRegistry.AtomRegistry, queries = yield* QueryWork;
  const state = yield* Ref.make<State<A>>({ closed: false, refreshToken: undefined, request: null, snapshot: emptyCatalog<A>() });
  const snapshot = Atom.make(Ref.get(state).pipe(Effect.map(value => value.snapshot)));
  const cleanupFailures = yield* Ref.make<readonly Cause.Reason<never>[]>([]);
  yield* Effect.addFinalizer(() => Ref.get(cleanupFailures).pipe(Effect.flatMap(reasons => reasons.length ? Effect.failCause(Cause.fromReasons(reasons)) : Effect.void)));
  const publish = Effect.sync(() => { if (!closed(root) && !closed(owner)) registry.refresh(snapshot); });
  const update = (change: (current: State<A>) => State<A>) => Ref.modify(state, current => { const next = change(current); return [next !== current && !next.closed, next] as const; }).pipe(Effect.flatMap(changed => changed ? publish : Effect.void));
  const queryScope = yield* Ref.make(yield* makeCatalogChildScope(owner));
  yield* Effect.addFinalizer(() => update(current => ({ ...current, closed: true, request: null })));
  const refresh = Effect.fn("Frontend.Catalog.refresh")(function* (expectedEpoch?: number) {
    if (closed(root) || closed(owner)) return;
    const request = {};
    const selected = yield* Ref.modify(state, current => current.closed || (expectedEpoch !== undefined && current.snapshot.selectionEpoch !== expectedEpoch)
      ? [null, current] as const
      : [current.snapshot, { ...current, request, snapshot: { ...current.snapshot, loading: current.snapshot.workspaceId !== null, error: null } }] as const);
    if (selected === null) return;
    yield* publish;
    const child = yield* makeCatalogChildScope(owner), previous = yield* Ref.getAndSet(queryScope, child);
    const cleanup = yield* Effect.exit(Scope.close(previous, Exit.void));
    if (Exit.isFailure(cleanup)) {
      yield* Ref.update(cleanupFailures, reasons => [...reasons, ...cleanup.cause.reasons]);
      return yield* cleanup;
    }
    const current = yield* Ref.get(state);
    if (current.closed || current.request !== request || closed(root) || closed(owner) || selected.workspaceId === null) return;
    const owns = (value: State<A>) => !value.closed && value.request === request && value.snapshot.selectionEpoch === selected.selectionEpoch;
    const failed = (message: string) => update(value => owns(value) ? { ...value, snapshot: { ...value.snapshot, loaded: true, error: message } } : value);
    // The query coordinator joins interruption, while its observer may consume
    // that Exit. Retain paired cleanup defects before that projection; ordinary
    // query failures still settle through the public resource error state.
    const operation = load(selected.workspaceId).pipe(Effect.onExit(exit => {
      if (Exit.isFailure(exit) && exit.cause.reasons.some(reason => reason._tag === "Interrupt")) {
        const defects = exit.cause.reasons.filter(reason => reason._tag === "Die");
        return Ref.update(cleanupFailures, reasons => [...reasons, ...defects]);
      }
      return Effect.void;
    }));
    const job = queries.observe(operation).pipe(
      Effect.matchEffect({ onFailure: error => failed(resourceFailureMessage(error)), onSuccess: value => update(current => owns(current) ? { ...current, snapshot: { ...current.snapshot, value, loaded: true, error: null } } : current) }),
      Effect.catchCause(() => failed(INTERNAL_HTTP_MESSAGE)),
      Effect.ensuring(update(current => owns(current) ? { ...current, request: null, snapshot: { ...current.snapshot, loading: false } } : current))
    );
    yield* Effect.forkIn(job, child);
  });
  const select = Effect.fn("Frontend.Catalog.select")(function* (selection: CatalogSelection, generation?: number) {
    if (closed(root) || closed(owner)) return;
    const epoch = yield* Ref.modify(state, current => current.closed || (generation === undefined
      ? current.snapshot.workspaceId === selection.workspaceId && current.refreshToken === selection.refreshToken
      : generation <= current.snapshot.selectionEpoch)
      ? [null, current] as const
      : [generation ?? current.snapshot.selectionEpoch + 1, { ...current, refreshToken: selection.refreshToken, request: null, snapshot: emptyCatalog<A>(selection.workspaceId, generation ?? current.snapshot.selectionEpoch + 1) }] as const);
    if (epoch === null) return;
    yield* publish;
    yield* refresh(epoch);
  });
  const clearError = update(current => current.closed || current.snapshot.error === null ? current : { ...current, snapshot: { ...current.snapshot, error: null } });
  return { snapshot, read: Ref.get(state).pipe(Effect.map(value => value.snapshot)), select, refresh: refresh(), clearError } satisfies CatalogSession<A>;
});
